import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LOCALE_STORAGE_KEY, rememberLocale, useLocale } from './LocaleContext';
import { localeFromPath, translatePath } from './routes';

const CHECKED_KEY = 'lpd.locale-checked';
// Les robots (Google compris) lisent les pages avec un navigateur anglais : on ne les
// redirige jamais, sinon la version française ne serait plus référencée.
const BOT_PATTERN = /bot|crawl|spider|slurp|facebookexternalhit|embedly|preview|lighthouse|headless|prerender/i;

function readStorage(storage, key) {
  try { return storage.getItem(key); } catch { return null; }
}

export function prefersEnglish(languages, stored) {
  if (stored === 'en') return true;
  if (stored === 'fr') return false;
  const list = (languages || []).filter(Boolean).map(language => language.toLowerCase());
  return list.length > 0 && !list.some(language => language.startsWith('fr'));
}

// Première page de la visite en français, navigateur dans une autre langue (ou choix « EN »
// mémorisé) : on bascule une seule fois vers la page anglaise équivalente.
export function LocaleAutoDetect() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (readStorage(sessionStorage, CHECKED_KEY)) return;
    try { sessionStorage.setItem(CHECKED_KEY, '1'); } catch { }
    if (typeof navigator === 'undefined' || BOT_PATTERN.test(navigator.userAgent || '')) return;
    if (localeFromPath(pathname) !== 'fr') return;
    const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
    if (!prefersEnglish(languages, readStorage(localStorage, LOCALE_STORAGE_KEY))) return;
    const target = translatePath(pathname, 'en');
    if (target !== pathname) navigate(`${target}${search}${hash}`, { replace: true });
    // Vérification unique, à l'arrivée sur le site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export function LanguageSwitcher({ className = '', onSwitch }) {
  const { locale, switchPath } = useLocale();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const choose = target => {
    rememberLocale(target);
    onSwitch?.();
    if (target !== locale) navigate(switchPath(`${pathname}${search}`, target));
  };

  return (
    <div className={`inline-flex items-center gap-1 font-sans text-xs font-semibold tracking-[0.14em] ${className}`} role="group" aria-label={locale === 'en' ? 'Language' : 'Langue'}>
      {['fr', 'en'].map((code, index) => (
        <React.Fragment key={code}>
          {index > 0 && <span aria-hidden="true" className="text-muted-foreground/50">·</span>}
          <button
            type="button"
            lang={code}
            onClick={() => choose(code)}
            aria-pressed={locale === code}
            aria-label={code === 'fr' ? 'Français' : 'English'}
            className={`rounded-full px-1.5 py-1 uppercase transition-colors duration-200 ${locale === code ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {code}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}
